'use client';

import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { Badge, SignatureBadgeItem } from '@/types/achievements';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import BadgeUnit from '@/components/features/dashboard/modal/DashboardAchievementBadgeUnit';
import Header from '@/components/modal/Header';

export default function DashboardAchievementBadge() {
    const [badges, setBadges] = useState<Badge[]>([]);
    const [totalAchievementCount, setTotalAchievementCount] = useState(0);
    const [totalAchievementScore, setTotalAchievementScore] = useState(0);
    const [selectedSignatureBadgeCd, setSelectedSignatureBadgeCd] = useState<string[]>([]);
    const { handleError } = useHandleError();
    const setUserBadges = useAuthStore((s) => s.setUserBadges);

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch('/dashboard/my_achievement/badges');
                setBadges(data);
                const signatureBadgesCd = data
                    .filter((badge: Badge) => badge.signature === 1)
                    .map((badge: Badge) => badge.badge_cd);
                setSelectedSignatureBadgeCd(signatureBadgesCd);

                const filteredBadges = data.filter((badge: Badge) => badge.award_date_time);
                const totalCount = filteredBadges.length;
                setTotalAchievementCount(totalCount);

                const totalScore = filteredBadges.reduce(
                    (acc: number, badge: Badge) => acc + (badge.achievement_score || 0),
                    0,
                );
                setTotalAchievementScore(totalScore);
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, []);

    const handleBadgeClick = async (badgeCd: string) => {
        try {
            const data = await apiFetch('/dashboard/my_achievement/badges/signature', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ signature_badge_cd: badgeCd }),
            });
            setSelectedSignatureBadgeCd((prev) => {
                const isSelected = prev.includes(badgeCd);
                if (isSelected) {
                    return prev.filter((cd) => cd !== badgeCd);
                } else if (prev.length < 3) {
                    return [...prev, badgeCd];
                }
                return prev;
            });
            setUserBadges(
                data.map((badge: SignatureBadgeItem) => ({
                    badgeCd: badge.badge_cd,
                    achievementScore: badge.achievement_score,
                    awardDateTime: badge.award_date_time,
                })),
            );
        } catch (error: unknown) {
            handleError(error);
        }
    };

    const isBadgeCheckVisible = (badge: Badge) => {
        return (
            selectedSignatureBadgeCd.includes(badge.badge_cd) ||
            (badge.award_date_time && selectedSignatureBadgeCd.length < 3)
        );
    };

    return (
        <>
            <div className="w-full h-full p-2 overflow-y-scroll">
                <div className="flex flex-col justify-start items-start w-full h-full p-2">
                    <div className="flex items-center w-full h-12">
                        <Header title="COLLECTION" subTitle="" />
                    </div>

                    <div className="flex items-center justify-between gap-3 w-full bg-color-co-gray-light rounded-xl px-4 py-3 mt-1">
                        <div className="flex items-center">
                            <span className="font-corp font-bold text-sm text-color-base">獲得バッジ</span>
                            <span className="font-montserrat font-bold text-2xl lg:text-3xl text-color-c-red px-2 lg:px-3">
                                {totalAchievementCount}
                            </span>
                            <span className="font-corp font-bold text-sm text-color-base">コ</span>
                        </div>
                        <div className="flex items-center">
                            <span className="font-corp font-bold text-sm text-color-base">バッジスコア</span>
                            <span className="font-montserrat font-bold text-2xl lg:text-3xl text-color-c-orange pl-2 lg:pl-3">
                                {totalAchievementScore}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center w-full font-notojp font-medium text-xs text-color-co-gray-dark px-1 pt-2.5 pb-4 leading-relaxed">
                        <IconAdaptive
                            Icon={Info}
                            strokeWidth={2.0}
                            baseSize={16}
                            breakpoints={{ md: 16 }}
                            className="mr-[1px] text-gray-500"
                        />
                        <p className="pl-1 pb-[1px]">
                            バッジは称号として３つまでセットできます。変更の際は現在選択しているバッジを先に解除してください。
                        </p>
                    </div>
                    <div className="container mx-auto">
                        {badges.length === 0 ? (
                            <div className="flex flex-col w-full h-full font-corp font-medium text-sm lg:text-base text-gray-500 text-center pb-20">
                                <div className="grid gap-1 items-stretch w-full pt-32 text-white-base">
                                    獲得したバッジはありません
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-8">
                                    {badges.map((badge, index) => {
                                        const isAwarded = !!badge?.award_date_time;
                                        const isSelected = selectedSignatureBadgeCd.includes(badge.badge_cd);
                                        return (
                                            <div key={'badge_' + index} className="w-full h-full">
                                                <div
                                                    className={`relative flex flex-col h-full w-full rounded-2xl p-4 ${
                                                        isSelected
                                                            ? 'border-2 border-color-dashboard-signature-achievement-selected'
                                                            : ''
                                                    } ${isAwarded ? 'bg-color-white-base' : 'bg-color-co-gray-light'}`}
                                                    style={
                                                        isSelected
                                                            ? undefined
                                                            : { border: '2px solid rgba(0, 0, 0, 0.12)' }
                                                    }
                                                >
                                                    {isBadgeCheckVisible(badge) && (
                                                        <div className="absolute top-3 left-3 z-10">
                                                            <label
                                                                className="relative flex items-center rounded-full cursor-pointer"
                                                                htmlFor={`select_signature_badge_${badge.badge_cd}`}
                                                                data-ripple-dark="true"
                                                                onClick={() => handleBadgeClick(badge.badge_cd)}
                                                            >
                                                                <input
                                                                    checked={selectedSignatureBadgeCd.includes(
                                                                        badge.badge_cd,
                                                                    )}
                                                                    readOnly
                                                                    id={`select_signature_badge_${badge.badge_cd}`}
                                                                    type="radio"
                                                                    value={badge.badge_cd}
                                                                    className="before:content[''] peer relative h-8 w-8 appearance-none
                                                                        border-2 border-gray-300 text-color-dashboard-signature-achievement-selected transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity
                                                                        checked:border-color-dashboard-signature-achievement-selected checked:before:bg-color-dashboard-signature-achievement-selected hover:before:opacity-10 cursor-pointer"
                                                                />
                                                                <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 opacity-0 transition-opacity peer-checked:opacity-100 cursor-pointer">
                                                                    <ImageAdaptive
                                                                        src="/images/common/check-select.svg"
                                                                        baseWidth={18}
                                                                    />
                                                                </div>
                                                            </label>
                                                        </div>
                                                    )}

                                                    <div className="flex flex-row items-center w-full gap-3 pt-8 mb-3">
                                                        <div className="flex items-center justify-center w-[96px] min-w-[96px] lg:w-[112px] lg:min-w-[112px] flex-none">
                                                            <BadgeUnit
                                                                badgeCd={badge?.badge_cd}
                                                                achievementScore={badge?.achievement_score}
                                                                isAwarded={isAwarded}
                                                            />
                                                        </div>

                                                        <div className="flex flex-col flex-grow w-full min-w-0">
                                                            <div
                                                                className={`font-corp font-bold text-base lg:text-lg leading-snug ${
                                                                    isAwarded
                                                                        ? 'text-color-base'
                                                                        : 'text-color-co-gray-dark'
                                                                }`}
                                                            >
                                                                {badge?.badge_name}
                                                            </div>
                                                            <div
                                                                className={`font-notojp font-medium text-xs pt-1.5 leading-relaxed ${
                                                                    isAwarded
                                                                        ? 'text-color-base'
                                                                        : 'text-color-co-gray-dark'
                                                                }`}
                                                            >
                                                                {badge?.badge_description}
                                                            </div>
                                                            <div className="flex items-center pt-2.5">
                                                                <div className="flex items-center rounded-full border border-color-co-gray-light bg-color-white-base px-3 py-0.5">
                                                                    <span className="font-corp font-bold text-color-base text-[10px] lg:text-xs">
                                                                        スコア
                                                                    </span>
                                                                    <span className="font-montserrat font-medium text-sm lg:text-base text-color-c-orange pl-2.5">
                                                                        {badge?.achievement_score}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 w-full mt-auto pt-3"
                                                        style={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}
                                                    >
                                                        <div className="flex items-center">
                                                            <ImageAdaptive
                                                                src="/images/achievement/rate.svg"
                                                                baseWidth={22}
                                                            />
                                                            <div className="flex items-center font-corp font-medium text-[11px] lg:text-xs text-color-co-gray-dark px-1">
                                                                スクーマーズの
                                                                <span className="font-cantarell font-bold text-xs lg:text-sm text-color-base px-1">
                                                                    {badge?.achievement_rate}%
                                                                </span>
                                                                が獲得しています
                                                            </div>
                                                        </div>
                                                        {isAwarded && (
                                                            <div className="font-montserrat font-medium text-[10px] lg:text-xs tracking-widest text-color-co-gray-dark whitespace-nowrap">
                                                                {badge?.award_date_time}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
